function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH23_PART14: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 219. Bookkeeping Exception Queue ─────────────────────────────────

  "Bookkeeping Exception Queue": {
    nodes: [
      trigger("Scheduled: Daily unmatched transaction review", "schedule", {
        schedule: "Every business day at 9:00 AM",
        howItStarts:
          "Runs daily to fetch unmatched and uncategorized transactions from QuickBooks, classify exception types via AI, and route them to the appropriate bookkeeper for resolution.",
        setup:
          "Connect your QuickBooks account. Configure Slack channels for bookkeeping team alerts and the Google Sheets exception log.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "QuickBooks: Fetch unmatched and uncategorized transactions",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/query?query=SELECT * FROM Purchase WHERE AccountRef IS NULL AND TxnDate >= '{yesterday}' MAXRESULTS 200",
            headers: [
              { key: "Authorization", value: "Bearer {quickbooks_token}" },
              { key: "Accept", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "QueryResponse.Purchase",
                customName: "unmatched_txns",
                type: "object",
              },
              {
                name: "QueryResponse.totalCount",
                customName: "unmatched_count",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Classify exception type and suggest GL coding for each transaction",
        480,
        "You are a bookkeeping specialist. For each unmatched transaction, classify the exception type (missing vendor, duplicate, incorrect amount, uncategorized, bank feed mismatch) and suggest the most likely GL account code and vendor match. Return exception_type, suggested_gl_code, suggested_vendor, confidence, resolution_action for each transaction. Also return summary counts by type.",
        "Classify these unmatched transactions: {unmatched_txns}",
        [
          { field: "classifications", as: "classifications", type: "object" },
          { field: "summary_by_type", as: "summary_by_type", type: "string" },
          { field: "high_value_count", as: "high_value_count", type: "number" },
          {
            field: "total_unmatched_amount",
            as: "total_unmatched_amount",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "runCode",
        "Route exceptions by type and value to appropriate bookkeeper",
        720,
        {
          codeDetails: {
            language: "javascript",
            code: "const txns = inputs.classifications; const routes = { vendor_issues: [], duplicates: [], uncategorized: [], high_value: [] }; for (const t of txns) { if (t.exception_type === 'missing_vendor') routes.vendor_issues.push(t); else if (t.exception_type === 'duplicate') routes.duplicates.push(t); else routes.uncategorized.push(t); if (parseFloat(t.amount) > 1000) routes.high_value.push(t); } return { vendor_count: routes.vendor_issues.length, duplicate_count: routes.duplicates.length, uncat_count: routes.uncategorized.length, has_high_value: routes.high_value.length > 0, high_value_total: routes.high_value.reduce((s, t) => s + parseFloat(t.amount), 0).toFixed(2) };",
          },
        },
      ),
      slackNode(
        "5",
        "Slack: Post exception queue summary to #bookkeeping",
        960,
        "Daily Bookkeeping Exception Queue\n\nTotal unmatched: {unmatched_count} transactions (${total_unmatched_amount})\n\nBreakdown:\n{summary_by_type}\n\nHigh-value exceptions (>$1,000): {high_value_count}\n\nPlease review and resolve in QuickBooks. Auto-suggested GL codes have been applied where confidence > 90%.",
      ),
      n(
        "6",
        "condition",
        "Check: Any unmatched transaction exceeds $1,000?",
        1200,
        {
          conditionDetails: {
            conditions: [
              { variable: "{has_high_value}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "7",
        "Email: Escalate high-value exceptions to finance manager",
        1440,
        "{finance_manager_email}",
        "Bookkeeping Alert: {high_value_count} high-value unmatched transactions (${high_value_total})",
        "Hello,\n\nThe daily bookkeeping exception scan found {high_value_count} unmatched transactions exceeding $1,000, totaling ${high_value_total}.\n\nThese require immediate attention to ensure accurate financial reporting.\n\nException summary:\n{summary_by_type}\n\nPlease review in QuickBooks or delegate to the bookkeeping team.\n\nFull exception log: {sheets_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log all exceptions to daily audit register",
        1680,
        "BookkeepingExceptions",
        '[["{timestamp}","{unmatched_count}","{total_unmatched_amount}","{vendor_count}","{duplicate_count}","{uncat_count}","{high_value_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Daily scan of QuickBooks for unmatched transactions, uses AI to classify exception types and suggest GL codes, routes by type and value, posts a summary to Slack, escalates high-value exceptions to the finance manager via email, and logs all exceptions to a spreadsheet.",
  },

  // ─── 220. Board Finance Packet Prep ───────────────────────────────────

  "Board Finance Packet Prep": {
    nodes: [
      trigger("Scheduled: Quarterly board packet preparation", "schedule", {
        schedule: "7 days before the end of each quarter at 8:00 AM",
        howItStarts:
          "Runs 7 days before quarter-end to pull financial data from QuickBooks and pipeline data from HubSpot, compile an AI-generated board summary, and prepare the slide deck for CFO review.",
        setup:
          "Connect QuickBooks, HubSpot CRM, and Google Slides. Configure the board packet template slide deck ID.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "QuickBooks: Fetch quarterly P&L, balance sheet, and cash flow",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/reports/ProfitAndLoss?start_date={quarter_start}&end_date={quarter_end}",
            headers: [
              { key: "Authorization", value: "Bearer {quickbooks_token}" },
              { key: "Accept", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "Rows", customName: "pnl_data", type: "object" },
              {
                name: "Header.ReportName",
                customName: "report_name",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch pipeline summary and closed-won totals",
        480,
        {
          hitApiDetails: {
            method: "GET",
            url: 'https://api.hubapi.com/crm/v3/objects/deals?properties=dealname,amount,dealstage,closedate&filterGroups=[{"filters":[{"propertyName":"closedate","operator":"GTE","value":"{quarter_start}"}]}]&limit=500',
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "results", customName: "quarter_deals", type: "object" },
              { name: "total", customName: "deal_count", type: "number" },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "OpenAI: Compile executive board summary with financial narrative",
        720,
        "You are a CFO preparing a quarterly board packet. Given P&L data, pipeline metrics, and deal activity, write a 3-paragraph executive summary covering: (1) financial performance vs plan, (2) sales pipeline health and bookings, (3) key risks and strategic recommendations. Also produce 5 bullet-point highlights for the slide deck. Return executive_summary, slide_highlights, revenue_total, expense_total, net_income, pipeline_value, bookings_total, yoy_growth_estimate.",
        "Compile board summary from: P&L: {pnl_data}, Deals: {quarter_deals}, Deal count: {deal_count}, Quarter: {quarter_label}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "slide_highlights", as: "slide_highlights", type: "string" },
          { field: "revenue_total", as: "revenue_total", type: "string" },
          { field: "net_income", as: "net_income", type: "string" },
          { field: "pipeline_value", as: "pipeline_value", type: "string" },
          { field: "bookings_total", as: "bookings_total", type: "string" },
        ],
      ),
      n(
        "5",
        "runCode",
        "Format financial charts and deck content for Google Slides",
        960,
        {
          codeDetails: {
            language: "javascript",
            code: "const revenue = inputs.revenue_total; const net = inputs.net_income; const pipeline = inputs.pipeline_value; const bookings = inputs.bookings_total; const highlights = inputs.slide_highlights; return { slide_title: inputs.quarter_label + ' Board Financial Review', slide_subtitle: 'Revenue: ' + revenue + ' | Net Income: ' + net, slide_body: highlights, chart_data: { revenue, net, pipeline, bookings }, deck_ready: true };",
          },
        },
      ),
      n(
        "6",
        "hitApi",
        "Google Slides: Create board deck from template with quarterly data",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://slides.googleapis.com/v1/presentations/{template_deck_id}:batchUpdate",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "requests",
                value:
                  '[{"replaceAllText":{"containsText":{"text":"{{QUARTER}}","matchCase":true},"replaceText":"{quarter_label}"}},{"replaceAllText":{"containsText":{"text":"{{REVENUE}}","matchCase":true},"replaceText":"{revenue_total}"}},{"replaceAllText":{"containsText":{"text":"{{NET_INCOME}}","matchCase":true},"replaceText":"{net_income}"}},{"replaceAllText":{"containsText":{"text":"{{HIGHLIGHTS}}","matchCase":true},"replaceText":"{slide_highlights}"}}]',
              },
            ],
            variables: [
              { name: "presentationId", customName: "deck_id", type: "string" },
            ],
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send board packet draft to CFO for review",
        1440,
        "{cfo_email}",
        "Board Finance Packet Ready for Review — {quarter_label}",
        "Hello,\n\nThe {quarter_label} board finance packet has been prepared and is ready for your review.\n\n{executive_summary}\n\nKey metrics:\n- Revenue: {revenue_total}\n- Net income: {net_income}\n- Pipeline: {pipeline_value}\n- Bookings: {bookings_total}\n\nSlide deck: {slides_url}\n\nPlease review and approve before the board meeting. Reply with any edits needed.",
      ),
      slackNode(
        "8",
        "Slack: Confirm board packet preparation complete",
        1680,
        "Board Packet Prep Complete — {quarter_label}\n\nThe quarterly finance packet has been compiled and sent to the CFO for review.\n\nRevenue: {revenue_total} | Net: {net_income}\nPipeline: {pipeline_value} | Bookings: {bookings_total}\n\nDeck: {slides_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Quarterly workflow that pulls financials from QuickBooks and pipeline from HubSpot, uses AI to write an executive board summary, populates a Google Slides deck template, emails the draft to the CFO, and confirms completion in Slack.",
  },

  // ─── 221. Password Reset Helper ───────────────────────────────────────

  "Password Reset Helper": {
    nodes: [
      trigger("Webhook: Password reset request submitted", "webhook_app", {
        source: "helpdesk",
        event: "password_reset_request",
        howItStarts:
          "Fires when an employee submits a password reset request through the IT helpdesk portal, providing the requestor email, employee ID, target system, and request timestamp.",
        setup:
          "Connect your helpdesk portal webhook and Okta admin API. Configure MFA verification settings.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract and verify identity details from reset request",
        240,
        "You are an IT security analyst. Extract the employee name, email, employee ID, target system requiring reset, department, and manager name from the reset request. Verify the request looks legitimate based on the requestor identity and target system. Return employee_name, employee_email, employee_id, target_system, department, is_legitimate, risk_flags.",
        "Verify this password reset request: {webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          { field: "target_system", as: "target_system", type: "string" },
          { field: "is_legitimate", as: "is_legitimate", type: "boolean" },
          { field: "risk_flags", as: "risk_flags", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Okta: Verify employee MFA status and send push challenge",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{okta_domain}/api/v1/users/{employee_id}/factors/{factor_id}/verify",
            headers: [
              { key: "Authorization", value: "SSWS {okta_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "factorResult",
                customName: "mfa_result",
                type: "string",
              },
              {
                name: "_links.poll.href",
                customName: "poll_url",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: MFA verification successful?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{mfa_result}", operator: "==", value: "SUCCESS" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "Okta: Execute password reset and generate temporary credentials",
        960,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{okta_domain}/api/v1/users/{employee_id}/lifecycle/reset_password?sendEmail=false",
            headers: [
              { key: "Authorization", value: "SSWS {okta_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "resetPasswordUrl",
                customName: "reset_url",
                type: "string",
              },
            ],
          },
        },
      ),
      emailNode(
        "6",
        "Email: Send secure reset link to employee",
        1200,
        "{employee_email}",
        "Password Reset — {target_system}",
        "Hello {employee_name},\n\nYour password reset request for {target_system} has been verified and approved.\n\nClick the secure link below to set a new password. This link expires in 30 minutes.\n\nReset link: {reset_url}\n\nPassword requirements:\n- Minimum 12 characters\n- At least one uppercase, lowercase, number, and special character\n- Cannot reuse last 10 passwords\n\nIf you did not request this reset, contact IT Security immediately.",
      ),
      slackNode(
        "7",
        "Slack: Log reset event to #it-security audit channel",
        1440,
        "Password Reset Completed\n\nEmployee: {employee_name} ({employee_email})\nSystem: {target_system}\nMFA verified: {mfa_result}\nTimestamp: {timestamp}\nRisk flags: {risk_flags}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Append to security audit trail",
        1680,
        "PasswordResets",
        '[["{timestamp}","{employee_name}","{employee_id}","{target_system}","{mfa_result}","{risk_flags}","{is_legitimate}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes password reset requests by verifying identity via AI, challenging with Okta MFA, executing the reset upon verification, sending a secure reset link to the employee, logging to Slack for security audit, and recording in the audit trail.",
  },

  // ─── 222. Self-Service Password Reset Bot ─────────────────────────────

  "Self-Service Password Reset Bot": {
    nodes: [
      trigger("Webhook: Chat-based password reset request", "webhook_app", {
        source: "chat",
        event: "message.received",
        howItStarts:
          "Fires when an employee sends a password reset request through the IT chatbot, providing the message text, sender identity, and channel information.",
        setup:
          "Connect the IT chatbot webhook and configure Duo Security MFA integration with Active Directory.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Parse identity and target system from chat message",
        240,
        "You are an IT helpdesk chatbot. Parse the employee's password reset request to extract their name, email, employee ID if mentioned, and the target system or application they need reset. Determine if the request is clear enough to proceed. Return employee_name, employee_email, target_system, request_clear, clarification_needed.",
        "Parse this password reset chat request: {message_text} from sender: {sender_identity}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "target_system", as: "target_system", type: "string" },
          { field: "request_clear", as: "request_clear", type: "boolean" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Duo Security: Send MFA push notification for identity verification",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api-{duo_host}/auth/v2/auth",
            headers: [
              { key: "Authorization", value: "Basic {duo_auth}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [
              { key: "username", value: "{employee_email}" },
              { key: "factor", value: "push" },
              { key: "device", value: "auto" },
            ],
            variables: [
              {
                name: "response.result",
                customName: "duo_result",
                type: "string",
              },
              {
                name: "response.status",
                customName: "duo_status",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Duo MFA push approved?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{duo_result}", operator: "==", value: "allow" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "Active Directory: Reset password and set temporary credentials",
        960,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://graph.microsoft.com/v1.0/users/{user_id}",
            headers: [
              { key: "Authorization", value: "Bearer {graph_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "passwordProfile",
                value:
                  '{"forceChangePasswordNextSignIn":true,"password":"{temp_password}"}',
              },
            ],
            variables: [
              { name: "id", customName: "ad_user_id", type: "string" },
            ],
          },
        },
      ),
      n(
        "6",
        "sendMessage",
        "Chat: Confirm reset and provide next steps to employee",
        1200,
        {
          sendMessageDetails: {
            message:
              "Your password for {target_system} has been reset successfully.\n\nA temporary password has been sent to your registered email. You will be required to change it on your next login.\n\nIf you did not make this request, please contact IT Security immediately at security@company.com.",
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Log self-service reset to #it-security",
        1440,
        "Self-Service Password Reset\n\nEmployee: {employee_name} ({employee_email})\nSystem: {target_system}\nDuo MFA: {duo_result}\nMethod: Chat bot\nTimestamp: {timestamp}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Append to password reset audit log",
        1680,
        "PasswordAudit",
        '[["{timestamp}","{employee_name}","{employee_email}","{target_system}","{duo_result}","chat_bot","self_service"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Chat-based self-service password reset that parses the request with AI, verifies identity via Duo MFA push, resets the password in Active Directory, confirms via chat, logs to Slack security channel, and records in the audit trail.",
  },

  // ─── 223. Suspicious Login Alert ──────────────────────────────────────

  "Suspicious Login Alert": {
    nodes: [
      trigger("Okta webhook: Authentication event detected", "webhook_app", {
        source: "okta",
        event: "user.session.start",
        howItStarts:
          "Fires on every Okta authentication event, providing user ID, IP address, geolocation, device fingerprint, authentication method, and timestamp for anomaly analysis.",
        setup:
          "Connect Okta System Log webhook. Configure baseline location and device patterns for anomaly detection.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze authentication event for anomaly signals",
        240,
        "You are a SOC analyst reviewing authentication events. Analyze the login event for anomaly signals including: impossible travel (login from different country within short time), new device fingerprint, unusual time of day, failed MFA attempts, VPN/TOR exit node IP. Score the risk from 0-100. Return user_email, user_name, login_ip, login_country, login_city, device_type, is_new_device, risk_score, anomaly_reasons, recommended_action.",
        "Analyze this Okta auth event for anomalies: {webhook_payload}, User history: {user_auth_history}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "login_country", as: "login_country", type: "string" },
          { field: "risk_score", as: "risk_score", type: "number" },
          { field: "anomaly_reasons", as: "anomaly_reasons", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Compute composite risk score with historical context",
        480,
        {
          codeDetails: {
            language: "javascript",
            code: "const ai_score = inputs.risk_score; const is_new_device = inputs.is_new_device; const login_country = inputs.login_country; const known_countries = inputs.known_countries || ['US', 'CA']; const foreign_login = !known_countries.includes(login_country); const adjusted_score = Math.min(100, ai_score + (is_new_device ? 15 : 0) + (foreign_login ? 20 : 0)); const is_high_risk = adjusted_score > 75; const needs_verification = adjusted_score > 50; return { adjusted_score, is_high_risk, needs_verification, foreign_login };",
          },
        },
      ),
      n("4", "condition", "Check: Risk score exceeds 75 (high risk)?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{is_high_risk}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send 'Was this you?' verification to the user",
        960,
        "{user_email}",
        "Security Alert: Unusual login detected on your account",
        "Hello {user_name},\n\nWe detected an unusual login to your account:\n\nTime: {timestamp}\nLocation: {login_city}, {login_country}\nDevice: {device_type}\n\nWas this you? If yes, no action is needed.\n\nIf this was NOT you, please:\n1. Click here to secure your account: {secure_account_url}\n2. Change your password immediately\n3. Contact IT Security at security@company.com\n\nRisk factors detected: {anomaly_reasons}",
      ),
      slackNode(
        "6",
        "Slack: Alert SOC team of high-risk authentication event",
        1200,
        "SUSPICIOUS LOGIN ALERT\n\nUser: {user_name} ({user_email})\nLocation: {login_city}, {login_country}\nRisk score: {adjusted_score}/100\nAnomalies: {anomaly_reasons}\nRecommended action: {recommended_action}\n\nVerification email sent to user. Monitor for response.",
      ),
      n(
        "7",
        "hitApi",
        "PagerDuty: Page on-call security engineer for critical risk",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://events.pagerduty.com/v2/enqueue",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "routing_key", value: "{pagerduty_routing_key}" },
              { key: "event_action", value: "trigger" },
              {
                key: "payload",
                value:
                  '{"summary":"Suspicious login: {user_name} from {login_country} (risk: {adjusted_score})","severity":"critical","source":"okta-auth-monitor","component":"authentication","custom_details":{"user":"{user_email}","risk_score":"{adjusted_score}","anomalies":"{anomaly_reasons}"}}',
              },
            ],
            variables: [
              { name: "dedup_key", customName: "incident_key", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log to security incident tracker",
        1680,
        "SecurityIncidents",
        '[["{timestamp}","{user_email}","{login_country}","{adjusted_score}","{anomaly_reasons}","{recommended_action}","{incident_key}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors Okta authentication events, uses AI to detect anomalies like impossible travel and new devices, computes a composite risk score, sends 'was this you?' emails for high-risk logins, alerts the SOC team in Slack, pages on-call security via PagerDuty, and logs incidents.",
  },

  // ─── 224. Vulnerability Scan Alert ────────────────────────────────────

  "Vulnerability Scan Alert": {
    nodes: [
      trigger(
        "Snyk webhook: New vulnerability findings published",
        "webhook_app",
        {
          source: "snyk",
          event: "project.snapshot",
          howItStarts:
            "Fires when Snyk publishes new vulnerability scan results for a monitored project, providing the project name, vulnerability list with CVE IDs, severity scores, and affected packages.",
          setup:
            "Connect your Snyk organization webhook. Configure severity thresholds and Jira project for ticket creation.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Deduplicate findings and enrich with exploit intelligence",
        240,
        "You are a vulnerability management analyst. Deduplicate the scan findings by CVE ID, classify each by severity (critical, high, medium, low), identify which are known to be actively exploited in the wild, and recommend remediation priority. Return unique_cves, critical_count, high_count, actively_exploited, top_priority_cve, remediation_summary, affected_packages.",
        "Analyze and deduplicate these Snyk vulnerability findings: {webhook_payload}",
        [
          { field: "unique_cves", as: "unique_cves", type: "object" },
          { field: "critical_count", as: "critical_count", type: "number" },
          { field: "high_count", as: "high_count", type: "number" },
          {
            field: "actively_exploited",
            as: "actively_exploited",
            type: "number",
          },
          { field: "top_priority_cve", as: "top_priority_cve", type: "string" },
          {
            field: "remediation_summary",
            as: "remediation_summary",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "NVD API: Enrich top CVEs with CVSS scores and vector strings",
        480,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://services.nvd.nist.gov/rest/json/cves/2.0?cveId={top_priority_cve}",
            headers: [{ key: "apiKey", value: "{nvd_api_key}" }],
            body: [],
            variables: [
              {
                name: "vulnerabilities[0].cve.metrics.cvssMetricV31[0].cvssData.baseScore",
                customName: "cvss_score",
                type: "number",
              },
              {
                name: "vulnerabilities[0].cve.metrics.cvssMetricV31[0].cvssData.vectorString",
                customName: "cvss_vector",
                type: "string",
              },
              {
                name: "vulnerabilities[0].cve.descriptions[0].value",
                customName: "cve_description",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "runCode",
        "Filter for critical and actively exploited vulnerabilities",
        720,
        {
          codeDetails: {
            language: "javascript",
            code: "const critical = inputs.critical_count; const exploited = inputs.actively_exploited; const cvss = inputs.cvss_score; const is_urgent = critical > 0 || exploited > 0 || cvss >= 9.0; const sla_hours = is_urgent ? 24 : cvss >= 7.0 ? 72 : 168; return { is_urgent, sla_hours, total_findings: critical + inputs.high_count, severity_label: is_urgent ? 'CRITICAL' : cvss >= 7.0 ? 'HIGH' : 'MEDIUM' };",
          },
        },
      ),
      n(
        "5",
        "condition",
        "Check: Actively exploited vulnerability found?",
        960,
        {
          conditionDetails: {
            conditions: [
              { variable: "{is_urgent}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Send urgent vulnerability alert to #security-ops",
        1200,
        "VULNERABILITY ALERT — {severity_label}\n\nProject: {project_name}\nCritical: {critical_count} | High: {high_count}\nActively exploited: {actively_exploited}\n\nTop priority: {top_priority_cve} (CVSS {cvss_score})\n{cve_description}\n\nRemediation SLA: {sla_hours} hours\n{remediation_summary}",
      ),
      n(
        "7",
        "hitApi",
        "Jira: Create vulnerability remediation ticket with SLA",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://your-domain.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"SEC"},"summary":"[{severity_label}] {top_priority_cve}: {affected_packages}","issuetype":{"name":"Bug"},"priority":{"name":"Highest"},"description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"CVE: {top_priority_cve}\\nCVSS: {cvss_score}\\nVector: {cvss_vector}\\nExploited in wild: {actively_exploited}\\nSLA: {sla_hours}h\\n\\nRemediation:\\n{remediation_summary}"}]}]},"duedate":"{sla_deadline}"}',
              },
            ],
            variables: [
              { name: "key", customName: "jira_key", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log to vulnerability SLA tracking dashboard",
        1680,
        "VulnSLATracker",
        '[["{timestamp}","{top_priority_cve}","{cvss_score}","{severity_label}","{actively_exploited}","{sla_hours}","{jira_key}","{project_name}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes Snyk vulnerability scan results, deduplicates by CVE and enriches with NVD CVSS data, filters for critical and actively exploited findings, sends urgent Slack alerts, creates Jira remediation tickets with SLA deadlines, and tracks in a compliance dashboard.",
  },

  // ─── 225. Secret Rotation Reminder ────────────────────────────────────

  "Secret Rotation Reminder": {
    nodes: [
      trigger("Scheduled: Daily secrets age compliance check", "schedule", {
        schedule: "Every business day at 7:00 AM",
        howItStarts:
          "Runs daily to query HashiCorp Vault for all secrets, check their age against the rotation policy, and remind owners of overdue rotations.",
        setup:
          "Connect HashiCorp Vault with a read-only policy token. Configure rotation policy (e.g., 90 days for API keys, 30 days for passwords).",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Vault: List all secrets and their metadata with creation dates",
        240,
        {
          hitApiDetails: {
            method: "LIST",
            url: "https://{vault_addr}/v1/secret/metadata/",
            headers: [{ key: "X-Vault-Token", value: "{vault_token}" }],
            body: [],
            variables: [
              { name: "data.keys", customName: "secret_paths", type: "object" },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Check secret ages against rotation policy and flag overdue",
        480,
        {
          codeDetails: {
            language: "javascript",
            code: "const secrets = inputs.secret_metadata || []; const policy_days = { api_key: 90, password: 30, certificate: 365, token: 60 }; const overdue = []; const warning = []; const now = Date.now(); for (const s of secrets) { const age_days = Math.ceil((now - new Date(s.created_time)) / 86400000); const max = policy_days[s.type] || 90; if (age_days > max) overdue.push({ ...s, age_days, max_days: max }); else if (age_days > max * 0.8) warning.push({ ...s, age_days, max_days: max }); } return { overdue_count: overdue.length, warning_count: warning.length, has_overdue: overdue.length > 0, overdue_summary: overdue.map(s => s.path + ' (' + s.age_days + 'd/' + s.max_days + 'd)').join(', ') };",
          },
        },
      ),
      n("4", "condition", "Check: Any secrets overdue for rotation?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{has_overdue}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send rotation reminder to secret owners",
        960,
        "{secret_owner_email}",
        "Action Required: {overdue_count} secret(s) overdue for rotation",
        "Hello,\n\nThe following secrets under your ownership are overdue for rotation per our security policy:\n\n{overdue_summary}\n\nPlease rotate these secrets within the next 48 hours. Rotation guides:\n- API keys: Generate new key, update consumers, revoke old key\n- Passwords: Use the password generator, update all references\n- Certificates: Submit CSR to CA, deploy new cert\n\nVault console: {vault_ui_url}\n\nFailure to rotate within 72 hours will trigger an automatic escalation to the security lead.",
      ),
      n("6", "delayAction", "Wait 72 hours for owner action", 1200, {
        delayDetails: { duration: 72, unit: "hours" },
      }),
      slackNode(
        "7",
        "Slack: Escalate unrotated secrets to security lead",
        1440,
        "SECRET ROTATION ESCALATION\n\n{overdue_count} secret(s) remain unrotated past the policy deadline + 72h grace period.\n\nOverdue: {overdue_summary}\n\nThis has been escalated per policy. Please coordinate forced rotation or exception approval.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log compliance status to secrets dashboard",
        1680,
        "SecretCompliance",
        '[["{timestamp}","{overdue_count}","{warning_count}","{overdue_summary}","{escalation_sent}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Daily check of HashiCorp Vault secrets against rotation policies, emails owners of overdue secrets, waits 72 hours for compliance, escalates to the security lead in Slack if unresolved, and logs compliance status to a dashboard.",
  },

  // ─── 226. Security Awareness Training Reminder ────────────────────────

  "Security Awareness Training Reminder": {
    nodes: [
      trigger("Scheduled: Daily training completion check", "schedule", {
        schedule: "Every business day at 8:00 AM",
        howItStarts:
          "Runs daily to fetch incomplete security awareness training assignments from KnowBe4, classify by role priority, and send personalized reminders to employees who are past due.",
        setup:
          "Connect KnowBe4 reporting API. Configure role-based priority rules and Slack channels for manager notifications.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "KnowBe4: Fetch all incomplete training assignments",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://us.api.knowbe4.com/v1/training/enrollments?status=not_started,in_progress&per_page=500",
            headers: [
              { key: "Authorization", value: "Bearer {knowbe4_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "data",
                customName: "incomplete_enrollments",
                type: "object",
              },
              {
                name: "meta.total",
                customName: "total_incomplete",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Classify by role priority and compute days overdue",
        480,
        {
          codeDetails: {
            language: "javascript",
            code: "const enrollments = inputs.incomplete_enrollments; const now = Date.now(); const results = []; for (const e of enrollments) { const due = new Date(e.due_date); const days_overdue = Math.ceil((now - due) / 86400000); const is_past_due = days_overdue > 0; const is_privileged = ['admin', 'engineer', 'finance'].includes(e.role); const priority = is_privileged && is_past_due ? 'critical' : is_past_due ? 'high' : 'standard'; results.push({ ...e, days_overdue, is_past_due, priority }); } const past_due = results.filter(r => r.is_past_due); return { past_due_count: past_due.length, critical_count: results.filter(r => r.priority === 'critical').length, has_past_due: past_due.length > 0, total_incomplete: enrollments.length };",
          },
        },
      ),
      n("4", "condition", "Check: Any training assignments past due?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{has_past_due}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send personalized training reminder to past-due employees",
        960,
        "{employee_email}",
        "Security Training Overdue — Please complete by {extended_deadline}",
        "Hello {employee_name},\n\nYour required security awareness training '{training_name}' was due on {due_date} and is now {days_overdue} day(s) overdue.\n\nThis training is mandatory per our security policy and covers important topics including phishing awareness, data handling, and incident reporting.\n\nComplete your training here: {training_url}\nEstimated time: {training_duration} minutes\n\nPlease complete this by {extended_deadline}. Continued non-compliance will be reported to your manager and HR.\n\nThank you for helping keep our organization secure.",
      ),
      n("6", "delayAction", "Wait 5 days for completion", 1200, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      slackNode(
        "7",
        "Slack: Escalate to managers for employees still non-compliant",
        1440,
        "Training Compliance Escalation\n\n{past_due_count} employee(s) have not completed required security training.\nCritical roles: {critical_count}\n\nManagers: Please follow up with your direct reports to ensure completion. Non-compliance affects our security posture and audit readiness.\n\nView full list: {compliance_dashboard_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log compliance metrics to training dashboard",
        1680,
        "TrainingCompliance",
        '[["{timestamp}","{total_incomplete}","{past_due_count}","{critical_count}","{completion_rate_pct}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Daily check of KnowBe4 for incomplete training, classifies by role priority, emails past-due employees with personalized reminders, waits 5 days, escalates to managers in Slack for non-compliance, and logs metrics to a compliance dashboard.",
  },

  // ─── 227. Incident Response Bot ───────────────────────────────────────

  "Incident Response Bot": {
    nodes: [
      trigger("Webhook: Security incident report submitted", "webhook_app", {
        source: "security_portal",
        event: "incident_report",
        howItStarts:
          "Fires when a security incident is reported through the incident portal, phishing button, or automated detection, providing incident details, reporter, affected systems, and initial severity assessment.",
        setup:
          "Connect the security incident portal webhook. Configure PagerDuty escalation policies and Slack war room channel naming conventions.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Classify incident severity and determine response playbook",
        240,
        "You are a security incident response coordinator. Classify the reported incident by type (phishing, malware, data breach, unauthorized access, DDoS, insider threat), severity (P1-critical, P2-high, P3-medium, P4-low), determine the appropriate response playbook, identify affected systems and data types, and recommend immediate containment actions. Return incident_type, severity, playbook_name, affected_systems, data_types_at_risk, containment_actions, estimated_impact, responder_team.",
        "Classify this security incident: {webhook_payload}",
        [
          { field: "incident_type", as: "incident_type", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          { field: "playbook_name", as: "playbook_name", type: "string" },
          { field: "affected_systems", as: "affected_systems", type: "string" },
          {
            field: "containment_actions",
            as: "containment_actions",
            type: "string",
          },
          { field: "responder_team", as: "responder_team", type: "string" },
        ],
      ),
      n("3", "hitApi", "PagerDuty: Page the on-call security responder", 480, {
        hitApiDetails: {
          method: "POST",
          url: "https://events.pagerduty.com/v2/enqueue",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "routing_key", value: "{pagerduty_security_key}" },
            { key: "event_action", value: "trigger" },
            {
              key: "payload",
              value:
                '{"summary":"[{severity}] {incident_type}: {affected_systems}","severity":"{pagerduty_severity}","source":"incident-response-bot","component":"{affected_systems}","custom_details":{"type":"{incident_type}","playbook":"{playbook_name}","containment":"{containment_actions}"}}',
            },
          ],
          variables: [
            { name: "dedup_key", customName: "incident_key", type: "string" },
          ],
        },
      }),
      slackNode(
        "4",
        "Slack: Create incident war room channel and post initial brief",
        720,
        "SECURITY INCIDENT — {severity}\n\nType: {incident_type}\nAffected systems: {affected_systems}\nPlaybook: {playbook_name}\n\nImmediate containment actions:\n{containment_actions}\n\nResponder team: {responder_team}\nPagerDuty incident: {incident_key}\n\nAll updates should be posted in this channel. Commander will be assigned shortly.",
      ),
      n(
        "5",
        "condition",
        "Check: Severity is critical (P1) requiring commander approval?",
        960,
        {
          conditionDetails: {
            conditions: [
              { variable: "{severity}", operator: "==", value: "P1" },
            ],
          },
        },
      ),
      n(
        "6",
        "approvalGate",
        "Approval: Incident commander authorizes containment actions",
        1200,
        {
          approvalDetails: {
            approvers: ["incident_commander"],
            instructions:
              "P1 Security Incident — Commander approval required.\n\nType: {incident_type}\nAffected: {affected_systems}\nContainment plan: {containment_actions}\n\nApprove to execute containment actions including potential service isolation.",
          },
        },
      ),
      n(
        "7",
        "hitApi",
        "AWS: Preserve forensic evidence by snapshotting affected instances",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://ec2.amazonaws.com/?Action=CreateSnapshot&VolumeId={affected_volume_id}&Description=Incident-{incident_key}-forensic-snapshot",
            headers: [
              { key: "Authorization", value: "{aws_sig_v4}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [],
            variables: [
              { name: "snapshotId", customName: "snapshot_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log incident to timeline and evidence chain",
        1680,
        "IncidentTimeline",
        '[["{timestamp}","{incident_key}","{incident_type}","{severity}","{affected_systems}","{playbook_name}","{snapshot_id}","{responder_team}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("5", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Processes security incident reports, uses AI to classify severity and select the response playbook, pages on-call via PagerDuty, creates a Slack war room, routes P1 incidents through commander approval, preserves AWS forensic evidence, and logs to an incident timeline.",
  },

  // ─── 228. Legal Hold Notification ─────────────────────────────────────

  "Legal Hold Notification": {
    nodes: [
      trigger("Webhook: Legal hold initiated by counsel", "webhook_app", {
        source: "legal_portal",
        event: "legal_hold_created",
        howItStarts:
          "Fires when outside or in-house counsel initiates a litigation hold, providing the matter name, custodian list, preservation scope, and hold effective date.",
        setup:
          "Connect the legal hold management portal webhook. Configure Microsoft Graph for custodian lookup and email delivery.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Microsoft Graph: Resolve custodian identities and mailbox details",
        240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://graph.microsoft.com/v1.0/$batch",
            headers: [
              { key: "Authorization", value: "Bearer {graph_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "requests", value: "{custodian_batch_requests}" }],
            variables: [
              {
                name: "responses",
                customName: "custodian_details",
                type: "object",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Generate legal hold instructions tailored to preservation scope",
        480,
        "You are a litigation support specialist. Given the matter name, preservation scope (email, documents, chat, databases), and custodian roles, generate clear and legally sound preservation instructions. Include what must be preserved, what actions are prohibited (deletion, modification, auto-archive changes), and how to acknowledge receipt. Return hold_subject, hold_body, preservation_scope_summary, prohibited_actions, acknowledgment_deadline.",
        "Generate legal hold notice for: Matter: {matter_name}, Scope: {preservation_scope}, Custodian count: {custodian_count}, Effective date: {hold_date}",
        [
          { field: "hold_subject", as: "hold_subject", type: "string" },
          { field: "hold_body", as: "hold_body", type: "string" },
          {
            field: "preservation_scope_summary",
            as: "scope_summary",
            type: "string",
          },
          {
            field: "acknowledgment_deadline",
            as: "ack_deadline",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "approvalGate",
        "Approval: Legal counsel reviews hold notice before distribution",
        720,
        {
          approvalDetails: {
            approvers: ["legal_counsel"],
            instructions:
              "Please review the legal hold notice before it is sent to {custodian_count} custodians.\n\nMatter: {matter_name}\nScope: {scope_summary}\nDeadline: {ack_deadline}\n\nApprove to distribute the hold notice.",
          },
        },
      ),
      emailNode(
        "5",
        "Email: Distribute legal hold notice to all custodians",
        960,
        "{custodian_emails}",
        "{hold_subject}",
        "{hold_body}",
      ),
      n(
        "6",
        "delayAction",
        "Wait 72 hours for acknowledgment responses",
        1200,
        { delayDetails: { duration: 72, unit: "hours" } },
      ),
      n(
        "7",
        "condition",
        "Check: All custodians acknowledged the hold?",
        1440,
        {
          conditionDetails: {
            conditions: [
              { variable: "{all_acknowledged}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      slackNode(
        "8",
        "Slack: Escalate unacknowledged custodians to legal team",
        1680,
        "LEGAL HOLD ESCALATION\n\nMatter: {matter_name}\n{unacked_count} custodian(s) have NOT acknowledged the legal hold after 72 hours.\n\nUnacknowledged: {unacked_names}\n\nLegal team: Please follow up directly. Failure to acknowledge may require manager escalation or IT-enforced preservation.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Manages legal hold notifications by resolving custodians via Microsoft Graph, generating AI-drafted preservation instructions, routing through counsel approval, distributing hold notices by email, waiting for acknowledgments, and escalating non-respondents in Slack.",
  },

  // ─── 229. Privacy Impact Assessment Workflow ──────────────────────────

  "Privacy Impact Assessment Workflow": {
    nodes: [
      trigger(
        "Webhook form: Privacy impact assessment request submitted",
        "webhook_form",
        {
          source: "form",
          event: "pia_request",
          howItStarts:
            "Fires when a team submits a privacy impact assessment request for a new system, feature, or data processing activity, providing system details, data types, processing purposes, and third-party involvement.",
          setup:
            "Create a PIA intake form with fields: system name, data types collected, processing purpose, data subjects, third-party processors, data retention period, and cross-border transfers.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract system details and perform initial risk screening",
        240,
        "You are a data protection officer. Extract the system name, data types (PII categories), processing purposes, data subjects (employees, customers, etc.), third-party processors, retention period, and cross-border transfer details. Perform an initial privacy risk screening and classify as high, medium, or low risk based on GDPR Article 35 criteria. Return system_name, data_types, processing_purpose, data_subjects, third_parties, retention_period, cross_border, risk_level, risk_factors, requires_dpia.",
        "Screen this PIA request: {webhook_payload}",
        [
          { field: "system_name", as: "system_name", type: "string" },
          { field: "data_types", as: "data_types", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          { field: "requires_dpia", as: "requires_dpia", type: "boolean" },
          { field: "cross_border", as: "cross_border", type: "boolean" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Score privacy risk and determine assessment depth",
        480,
        {
          codeDetails: {
            language: "javascript",
            code: "const risk = inputs.risk_level; const dpia = inputs.requires_dpia; const cross_border = inputs.cross_border; const special_categories = inputs.data_types?.includes('health') || inputs.data_types?.includes('biometric'); const risk_score = (risk === 'high' ? 80 : risk === 'medium' ? 50 : 20) + (cross_border ? 15 : 0) + (special_categories ? 20 : 0); const is_high_risk = risk_score >= 70; return { risk_score: Math.min(100, risk_score), is_high_risk, assessment_type: is_high_risk ? 'Full DPIA' : 'Standard PIA', requires_dpo_review: is_high_risk || dpia };",
          },
        },
      ),
      aiNode(
        "4",
        "OpenAI: Generate tailored privacy assessment questionnaire",
        720,
        "You are a privacy compliance expert. Based on the system details and risk level, generate a tailored privacy impact assessment questionnaire with 8-12 questions covering: data minimization, lawful basis, consent mechanisms, access controls, encryption, retention automation, breach notification procedures, and third-party DPA status. Return questionnaire_items, recommended_controls, compliance_gaps.",
        "Generate PIA questionnaire for: System: {system_name}, Risk: {risk_level} (score: {risk_score}), Data types: {data_types}, Cross-border: {cross_border}",
        [
          { field: "questionnaire_items", as: "questionnaire", type: "string" },
          {
            field: "recommended_controls",
            as: "recommended_controls",
            type: "string",
          },
          { field: "compliance_gaps", as: "compliance_gaps", type: "string" },
        ],
      ),
      n("5", "condition", "Check: High risk requiring DPO review?", 960, {
        conditionDetails: {
          conditions: [
            {
              variable: "{requires_dpo_review}",
              operator: "==",
              value: "true",
            },
          ],
        },
      }),
      n(
        "6",
        "approvalGate",
        "Approval: DPO reviews high-risk privacy impact assessment",
        1200,
        {
          approvalDetails: {
            approvers: ["dpo"],
            instructions:
              "High-risk Privacy Impact Assessment requires DPO review.\n\nSystem: {system_name}\nRisk score: {risk_score}/100\nAssessment type: {assessment_type}\nData types: {data_types}\nRisk factors: {risk_factors}\n\nRecommended controls: {recommended_controls}\nCompliance gaps: {compliance_gaps}\n\nApprove, request modifications, or reject.",
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send assessment questionnaire and findings to stakeholders",
        1440,
        "{requestor_email}",
        "Privacy Impact Assessment — {system_name} ({assessment_type})",
        "Hello,\n\nYour privacy impact assessment for {system_name} has been processed.\n\nRisk level: {risk_level} (score: {risk_score}/100)\nAssessment type: {assessment_type}\n\nQuestionnaire:\n{questionnaire}\n\nRecommended controls:\n{recommended_controls}\n\nIdentified gaps:\n{compliance_gaps}\n\nPlease complete the questionnaire and address identified gaps before proceeding with implementation.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log PIA to privacy registry",
        1680,
        "PIARegistry",
        '[["{timestamp}","{system_name}","{data_types}","{risk_level}","{risk_score}","{assessment_type}","{requires_dpo_review}","{dpo_decision}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes privacy impact assessment requests, uses AI for risk screening and questionnaire generation, routes high-risk assessments through DPO approval, delivers the assessment and recommended controls to stakeholders, and logs to the PIA registry.",
  },

  // ─── 230. SOC2 Evidence Collector ──────────────────────────────────────

  "SOC2 Evidence Collector": {
    nodes: [
      trigger("Scheduled: Weekly SOC2 evidence collection", "schedule", {
        schedule: "Every Monday at 6:00 AM",
        howItStarts:
          "Runs weekly to automatically collect compliance evidence from AWS Config, GitHub access logs, and Google Drive HR policies, then map artifacts to SOC2 Trust Services Criteria.",
        setup:
          "Connect AWS Config, GitHub Enterprise, and Google Drive. Configure the SOC2 evidence mapping spreadsheet with TSC control IDs.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "AWS Config: Snapshot current compliance rules and conformance packs",
        240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://config.amazonaws.com/",
            headers: [
              { key: "Authorization", value: "{aws_sig_v4}" },
              { key: "Content-Type", value: "application/x-amz-json-1.1" },
              {
                key: "X-Amz-Target",
                value: "StarlingDoveService.GetComplianceSummaryByConfigRule",
              },
            ],
            body: [],
            variables: [
              {
                name: "ComplianceSummariesByConfigRule",
                customName: "config_compliance",
                type: "object",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "GitHub: Fetch organization access logs and permission changes",
        480,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.github.com/orgs/{org}/audit-log?phrase=action:org+created:>{last_week_date}&per_page=100",
            headers: [
              { key: "Authorization", value: "Bearer {github_token}" },
              { key: "Accept", value: "application/vnd.github+json" },
            ],
            body: [],
            variables: [
              { name: "", customName: "github_audit_logs", type: "object" },
            ],
          },
        },
      ),
      n(
        "4",
        "runCode",
        "Deduplicate artifacts and check for collection gaps",
        720,
        {
          codeDetails: {
            language: "javascript",
            code: "const aws_rules = inputs.config_compliance?.length || 0; const github_events = inputs.github_audit_logs?.length || 0; const expected_controls = 40; const collected = aws_rules + github_events; const coverage_pct = Math.round((collected / expected_controls) * 100); const has_gaps = coverage_pct < 90; const gaps = []; if (aws_rules === 0) gaps.push('AWS Config'); if (github_events === 0) gaps.push('GitHub audit logs'); return { coverage_pct, has_gaps, gap_systems: gaps.join(', '), total_artifacts: collected, aws_rules, github_events };",
          },
        },
      ),
      aiNode(
        "5",
        "OpenAI: Map collected evidence to SOC2 Trust Services Criteria",
        960,
        "You are a SOC2 compliance auditor. Map the collected evidence artifacts (AWS Config compliance rules, GitHub access logs, HR policies) to the appropriate SOC2 Trust Services Criteria (CC1-CC9 for Common Criteria, plus Availability, Confidentiality, Processing Integrity, Privacy). Identify which TSC controls have evidence coverage and which have gaps. Return tsc_mapping, covered_controls, gap_controls, gap_count, evidence_summary.",
        "Map evidence to SOC2 TSC: AWS Config rules: {aws_rules} compliant, GitHub events: {github_events}, Coverage: {coverage_pct}%",
        [
          { field: "covered_controls", as: "covered_controls", type: "string" },
          { field: "gap_controls", as: "gap_controls", type: "string" },
          { field: "gap_count", as: "gap_count", type: "number" },
          { field: "evidence_summary", as: "evidence_summary", type: "string" },
        ],
      ),
      n("6", "condition", "Check: Evidence coverage gaps detected?", 1200, {
        conditionDetails: {
          conditions: [
            { variable: "{has_gaps}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Alert control owners about evidence gaps",
        1440,
        "SOC2 Evidence Collection — Weekly Report\n\nCoverage: {coverage_pct}%\nTotal artifacts: {total_artifacts}\nGaps: {gap_count} control(s) missing evidence\n\nGap systems: {gap_systems}\nGap controls: {gap_controls}\n\nControl owners: Please upload missing evidence to the shared drive by Friday.\n\nEvidence index: {sheets_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Update evidence index with this week's collection",
        1680,
        "SOC2EvidenceIndex",
        '[["{timestamp}","{coverage_pct}","{total_artifacts}","{aws_rules}","{github_events}","{gap_count}","{gap_controls}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Weekly automated SOC2 evidence collection from AWS Config and GitHub, deduplicates artifacts, uses AI to map evidence to Trust Services Criteria, identifies coverage gaps, alerts control owners in Slack, and updates the evidence index spreadsheet.",
  },

  // ─── 231. Third-Party Risk Assessment ─────────────────────────────────

  "Third-Party Risk Assessment": {
    nodes: [
      trigger(
        "Webhook form: New vendor risk assessment requested",
        "webhook_form",
        {
          source: "form",
          event: "vendor_risk_request",
          howItStarts:
            "Fires when a department requests a security risk assessment for a new third-party vendor, providing vendor name, service description, data access requirements, and contract value.",
          setup:
            "Create a vendor risk intake form. Connect SecurityScorecard API for automated scoring and configure the risk register spreadsheet.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "SecurityScorecard: Pull vendor's external security rating",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.securityscorecard.io/companies/{vendor_domain}",
            headers: [
              { key: "Authorization", value: "Token {ssc_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "overall_grade",
                customName: "ssc_grade",
                type: "string",
              },
              {
                name: "overall_score",
                customName: "ssc_score",
                type: "number",
              },
              {
                name: "factor_grades",
                customName: "factor_details",
                type: "object",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Generate tailored security questionnaire based on vendor profile",
        480,
        "You are a third-party risk management specialist. Based on the vendor's SecurityScorecard rating, service type, and data access level, generate a targeted security questionnaire covering: data encryption (at rest and in transit), access controls, incident response plan, business continuity, subprocessor management, compliance certifications (SOC2, ISO 27001), and data retention/deletion policies. Prioritize questions based on the vendor's weak areas from the scorecard. Return questionnaire_text, focus_areas, risk_tier, estimated_review_time.",
        "Generate vendor questionnaire: Vendor: {vendor_name}, SSC grade: {ssc_grade} ({ssc_score}), Service: {service_description}, Data access: {data_access_level}",
        [
          { field: "questionnaire_text", as: "questionnaire", type: "string" },
          { field: "focus_areas", as: "focus_areas", type: "string" },
          { field: "risk_tier", as: "risk_tier", type: "string" },
          { field: "estimated_review_time", as: "review_time", type: "string" },
        ],
      ),
      emailNode(
        "4",
        "Email: Distribute security questionnaire to vendor contact",
        720,
        "{vendor_contact_email}",
        "Security Assessment Questionnaire — {vendor_name}",
        "Hello,\n\nAs part of our vendor onboarding process, we require completion of the following security questionnaire before we can proceed with the engagement.\n\n{questionnaire}\n\nPlease also provide copies of:\n- SOC2 Type II report (if available)\n- ISO 27001 certificate (if available)\n- Cyber insurance certificate\n- Data processing agreement\n\nPlease return the completed questionnaire within 10 business days.\n\nIf you have questions, contact our security team at security@company.com.",
      ),
      aiNode(
        "5",
        "OpenAI: Analyze vendor responses and compute risk score",
        960,
        "You are a security risk analyst. Analyze the vendor's completed questionnaire responses against our security requirements. Score each area (encryption, access control, incident response, compliance, data handling) on a 1-10 scale. Compute an overall risk score and recommendation (approve, conditional approve, reject). Return area_scores, overall_risk_score, recommendation, conditions, critical_findings.",
        "Analyze vendor questionnaire responses: {vendor_responses}, SSC score: {ssc_score}, Focus areas: {focus_areas}",
        [
          {
            field: "overall_risk_score",
            as: "vendor_risk_score",
            type: "number",
          },
          { field: "recommendation", as: "recommendation", type: "string" },
          { field: "conditions", as: "conditions", type: "string" },
          {
            field: "critical_findings",
            as: "critical_findings",
            type: "string",
          },
        ],
      ),
      n("6", "runCode", "Compile risk assessment report", 1200, {
        codeDetails: {
          language: "javascript",
          code: "const ssc = inputs.ssc_score; const risk = inputs.vendor_risk_score; const combined = Math.round((ssc * 0.4 + risk * 6) / 2); const tier = combined >= 80 ? 'low' : combined >= 50 ? 'medium' : 'high'; const approved = tier !== 'high' || inputs.recommendation === 'conditional approve'; return { combined_score: combined, risk_tier: tier, approved, needs_security_approval: tier === 'high' };",
        },
      }),
      n(
        "7",
        "approvalGate",
        "Approval: Security team reviews high-risk vendor assessment",
        1440,
        {
          approvalDetails: {
            approvers: ["security_lead"],
            instructions:
              "Third-party risk assessment requires security approval.\n\nVendor: {vendor_name}\nSSC grade: {ssc_grade} ({ssc_score})\nRisk score: {vendor_risk_score}/100\nRisk tier: {risk_tier}\nRecommendation: {recommendation}\n\nConditions: {conditions}\nCritical findings: {critical_findings}\n\nApprove with conditions or reject.",
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log to vendor risk register",
        1680,
        "VendorRiskRegister",
        '[["{timestamp}","{vendor_name}","{ssc_grade}","{ssc_score}","{vendor_risk_score}","{risk_tier}","{recommendation}","{critical_findings}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Assesses third-party vendor security risk by pulling SecurityScorecard ratings, generating a tailored questionnaire via AI, distributing to the vendor, analyzing responses, computing a combined risk score, routing high-risk vendors through security approval, and logging to the risk register.",
  },

  // ─── 232. Vendor Security Question Bot ────────────────────────────────

  "Vendor Security Question Bot": {
    nodes: [
      trigger(
        "Webhook: Inbound vendor security question received",
        "webhook_app",
        {
          source: "helpdesk",
          event: "security_question",
          howItStarts:
            "Fires when a sales or procurement team member submits a vendor's security question that needs an answer, providing the vendor name, question text, and the originating context (RFP, due diligence, etc.).",
          setup:
            "Connect the security question intake webhook. Configure the knowledge base of past assessments and standard security responses.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract the vendor identity and core security question",
        240,
        "You are a security compliance specialist. Extract the vendor name, the specific security question being asked, the context (RFP, due diligence, customer questionnaire), and identify the security domain (encryption, access control, incident response, compliance, privacy, infrastructure). Return vendor_name, question_text, context_type, security_domain, question_category.",
        "Parse this vendor security question: {webhook_payload}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "question_text", as: "question_text", type: "string" },
          { field: "context_type", as: "context_type", type: "string" },
          { field: "security_domain", as: "security_domain", type: "string" },
          {
            field: "question_category",
            as: "question_category",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Knowledge Base: Search existing assessment answers for similar questions",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.pinecone.io/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "namespace", value: "security-responses" },
              { key: "topK", value: "5" },
              { key: "includeMetadata", value: "true" },
              { key: "vector", value: "{question_embedding}" },
            ],
            variables: [
              {
                name: "matches",
                customName: "similar_answers",
                type: "object",
              },
              {
                name: "matches[0].score",
                customName: "top_match_score",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "OpenAI: Synthesize answer from existing responses and company policies",
        720,
        "You are a security compliance expert answering vendor security questions on behalf of the company. Using the similar past answers and our security policies, synthesize a clear, accurate, and professional response. Be specific about our practices (encryption standards, compliance certifications, incident response SLAs). Rate your confidence in the answer. Return answer_text, confidence_score, sources_used, needs_review.",
        "Answer this security question: {question_text}. Domain: {security_domain}. Similar past answers: {similar_answers}",
        [
          { field: "answer_text", as: "answer_text", type: "string" },
          { field: "confidence_score", as: "confidence", type: "number" },
          { field: "sources_used", as: "sources", type: "string" },
          { field: "needs_review", as: "needs_review", type: "boolean" },
        ],
      ),
      n("5", "condition", "Check: Answer confidence exceeds 70%?", 960, {
        conditionDetails: {
          conditions: [
            { variable: "{confidence}", operator: ">=", value: "70" },
          ],
        },
      }),
      n(
        "6",
        "sendMessage",
        "Chat: Deliver high-confidence answer to requestor",
        1200,
        {
          sendMessageDetails: {
            message:
              "Security Question Answer for {vendor_name}\n\nQuestion: {question_text}\n\nAnswer:\n{answer_text}\n\nConfidence: {confidence}%\nSources: {sources}\n\nPlease review before sharing with the vendor. If edits are needed, reply in this thread.",
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Escalate low-confidence questions to security team",
        1440,
        "Security Question — Manual Review Needed\n\nVendor: {vendor_name}\nQuestion: {question_text}\nDomain: {security_domain}\nContext: {context_type}\n\nDraft answer (confidence {confidence}%):\n{answer_text}\n\nSources: {sources}\n\nPlease review, edit, and approve before sending to the requestor.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log question and response to security Q&A database",
        1680,
        "SecurityQA",
        '[["{timestamp}","{vendor_name}","{question_category}","{security_domain}","{confidence}","{needs_review}","{context_type}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
    summary:
      "Processes vendor security questions by extracting the query, searching a knowledge base of past answers, synthesizing a response via AI, delivering high-confidence answers directly, escalating low-confidence ones to the security team in Slack, and logging to the Q&A database.",
  },

  // ─── 233. Security Policy Acknowledgment ──────────────────────────────

  "Security Policy Acknowledgment": {
    nodes: [
      trigger(
        "Webhook: New security policy published for acknowledgment",
        "webhook_app",
        {
          source: "policy_portal",
          event: "policy_published",
          howItStarts:
            "Fires when a new or updated security policy is published and requires employee acknowledgment, providing the policy name, version, effective date, and target audience.",
          setup:
            "Connect the policy management portal webhook. Configure Microsoft Graph for employee directory lookup and email distribution.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Microsoft Graph: Resolve target audience to employee email list",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://graph.microsoft.com/v1.0/groups/{target_group_id}/members?$select=displayName,mail,department,jobTitle&$top=999",
            headers: [
              { key: "Authorization", value: "Bearer {graph_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "value", customName: "audience_members", type: "object" },
              {
                name: "@odata.count",
                customName: "audience_count",
                type: "number",
              },
            ],
          },
        },
      ),
      emailNode(
        "3",
        "Email: Send policy acknowledgment request to all employees",
        480,
        "{audience_emails}",
        "Action Required: Acknowledge {policy_name} (v{policy_version})",
        "Hello,\n\nA new/updated security policy requires your acknowledgment.\n\nPolicy: {policy_name}\nVersion: {policy_version}\nEffective date: {effective_date}\n\nPlease review the policy and confirm your acknowledgment by clicking the link below:\n\nReview and acknowledge: {acknowledgment_url}\n\nDeadline: {ack_deadline}\n\nThis is a mandatory compliance requirement. Failure to acknowledge by the deadline will be reported to your manager.\n\nIf you have questions about the policy, contact security@company.com.",
      ),
      n("4", "delayAction", "Wait 72 hours for acknowledgment responses", 720, {
        delayDetails: { duration: 72, unit: "hours" },
      }),
      n(
        "5",
        "runCode",
        "Track acknowledgment completion rate by department",
        960,
        {
          codeDetails: {
            language: "javascript",
            code: "const total = inputs.audience_count; const acknowledged = inputs.ack_count || 0; const completion_pct = Math.round((acknowledged / total) * 100); const by_dept = inputs.dept_completion || {}; const low_depts = Object.entries(by_dept).filter(([d, pct]) => pct < 90).map(([d]) => d); return { completion_pct, pending_count: total - acknowledged, has_low_depts: low_depts.length > 0, low_dept_names: low_depts.join(', '), all_complete: completion_pct >= 100 };",
          },
        },
      ),
      n(
        "6",
        "condition",
        "Check: Any department below 90% acknowledgment?",
        1200,
        {
          conditionDetails: {
            conditions: [
              { variable: "{has_low_depts}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Remind managers of departments with low acknowledgment rates",
        1440,
        "Policy Acknowledgment Reminder\n\nPolicy: {policy_name} (v{policy_version})\nOverall completion: {completion_pct}%\nPending: {pending_count} employee(s)\n\nDepartments below 90%: {low_dept_names}\n\nManagers: Please remind your teams to acknowledge the policy by {ack_deadline}.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Export acknowledgment audit data",
        1680,
        "PolicyAcknowledgments",
        '[["{timestamp}","{policy_name}","{policy_version}","{audience_count}","{completion_pct}","{pending_count}","{low_dept_names}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Distributes security policy acknowledgment requests to employees resolved via Microsoft Graph, tracks completion rates by department after 72 hours, reminds managers in Slack for departments below 90%, and exports audit data to a compliance spreadsheet.",
  },

  // ─── 234. Device Provisioning Workflow ────────────────────────────────

  "Device Provisioning Workflow": {
    nodes: [
      trigger(
        "BambooHR webhook: New hire start date approaching (7 days out)",
        "webhook_app",
        {
          source: "bamboohr",
          event: "employee.start_date_approaching",
          howItStarts:
            "Fires 7 days before a new hire's start date, providing employee name, email, role, department, location (office vs remote), and manager details for equipment provisioning.",
          setup:
            "Connect BambooHR with start date monitoring. Configure Jira IT project for provisioning tickets and equipment inventory spreadsheet.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "runCode",
        "Determine equipment needs based on role and location",
        240,
        {
          codeDetails: {
            language: "javascript",
            code: "const role = inputs.role; const dept = inputs.department; const remote = inputs.is_remote; const equipment = ['MacBook Pro 14\"', 'Mouse', 'Keyboard']; if (['engineering', 'design'].includes(dept)) equipment.push('External Monitor 27\"', 'USB-C Hub'); if (remote) equipment.push('Home Office Stipend ($500)', 'Webcam HD', 'Headset'); if (dept === 'sales') equipment.push('Headset', 'Second Monitor'); return { equipment_list: equipment, item_count: equipment.length, is_remote: remote, needs_shipping: remote, estimated_cost: equipment.length * 200 };",
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "Jira: Create device provisioning ticket with equipment list",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://your-domain.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"IT"},"summary":"Device provisioning: {employee_name} — Start {start_date}","issuetype":{"name":"Task"},"priority":{"name":"High"},"description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"New hire: {employee_name}\\nRole: {role}\\nDepartment: {department}\\nLocation: {location}\\nStart date: {start_date}\\n\\nEquipment needed:\\n{equipment_list_formatted}\\n\\nEstimated cost: ${estimated_cost}"}]}]},"duedate":"{start_date_minus_2}"}',
              },
            ],
            variables: [
              { name: "key", customName: "jira_key", type: "string" },
            ],
          },
        },
      ),
      slackNode(
        "4",
        "Slack: Notify IT team of new provisioning request",
        720,
        "New Device Provisioning Request\n\nEmployee: {employee_name}\nRole: {role} ({department})\nStart date: {start_date}\nLocation: {location}\n\nEquipment ({item_count} items):\n{equipment_list_formatted}\n\nJira ticket: {jira_key}\nDeadline: {start_date_minus_2}",
      ),
      n("5", "condition", "Check: Remote employee requiring shipping?", 960, {
        conditionDetails: {
          conditions: [
            { variable: "{needs_shipping}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Send shipping details and tracking to new hire",
        1200,
        "{employee_email}",
        "Your equipment is on the way — Welcome to {company_name}!",
        "Hello {employee_name},\n\nWelcome! We're shipping your work equipment to arrive before your start date ({start_date}).\n\nEquipment being shipped:\n{equipment_list_formatted}\n\nShipping to: {shipping_address}\nTracking number: {tracking_number}\nExpected delivery: {delivery_date}\n\nSetup instructions will be included in the package. If you have questions, your IT onboarding buddy is {it_buddy_name} ({it_buddy_email}).\n\nWe're excited to have you join the team!",
      ),
      n(
        "7",
        "runCode",
        "Verify all equipment provisioned and update inventory",
        1440,
        {
          codeDetails: {
            language: "javascript",
            code: "const items = inputs.equipment_list; const provisioned = inputs.provisioned_items || []; const all_ready = items.length === provisioned.length; const missing = items.filter(i => !provisioned.includes(i)); return { all_provisioned: all_ready, missing_items: missing.join(', '), missing_count: missing.length };",
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Update equipment inventory and provisioning log",
        1680,
        "EquipmentInventory",
        '[["{timestamp}","{employee_name}","{role}","{department}","{item_count}","{estimated_cost}","{jira_key}","{all_provisioned}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers 7 days before a new hire's start date via BambooHR, determines equipment needs by role and location, creates a Jira provisioning ticket, notifies IT via Slack, ships equipment to remote employees with tracking, verifies completion, and updates inventory.",
  },

  // ─── 235. Access Request + Approval ───────────────────────────────────

  "Access Request + Approval": {
    nodes: [
      trigger(
        "Slack webhook: Access request submitted via IT bot",
        "webhook_app",
        {
          source: "slack",
          event: "access_request",
          howItStarts:
            "Fires when an employee submits an access request through the Slack IT bot, providing the requestor, target system, access level (read, write, admin), business justification, and duration.",
          setup:
            "Connect Slack IT bot webhook. Configure Okta admin API for provisioning and role-based access control policies.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "runCode",
        "Validate request against role-based access policies",
        240,
        {
          codeDetails: {
            language: "javascript",
            code: "const role = inputs.requestor_role; const target = inputs.target_system; const level = inputs.access_level; const rbac = inputs.rbac_policies || {}; const allowed = rbac[role]?.[target] || 'none'; const is_privileged = level === 'admin' || target.includes('production'); const auto_approve = allowed === level && !is_privileged; const needs_manager = !auto_approve; const needs_security = is_privileged; return { auto_approve, needs_manager, needs_security, is_privileged, approval_chain: is_privileged ? 'manager + security' : needs_manager ? 'manager' : 'auto', policy_match: allowed === level };",
          },
        },
      ),
      n(
        "3",
        "condition",
        "Check: Privileged access requiring security + manager approval?",
        480,
        {
          conditionDetails: {
            conditions: [
              { variable: "{is_privileged}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      n(
        "4",
        "approvalGate",
        "Approval: Manager and security review privileged access request",
        720,
        {
          approvalDetails: {
            approvers: ["manager", "security_team"],
            instructions:
              "Privileged access request requires dual approval.\n\nRequestor: {requestor_name} ({requestor_role})\nTarget system: {target_system}\nAccess level: {access_level}\nJustification: {business_justification}\nDuration: {access_duration}\n\nThis is a privileged access request. Please verify business need and approve or reject.",
          },
        },
      ),
      n(
        "5",
        "hitApi",
        "Okta: Provision access by adding user to appropriate group",
        960,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{okta_domain}/api/v1/groups/{target_group_id}/users/{user_id}",
            headers: [
              { key: "Authorization", value: "SSWS {okta_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "id", customName: "provisioning_id", type: "string" },
            ],
          },
        },
      ),
      emailNode(
        "6",
        "Email: Confirm access provisioned to requestor",
        1200,
        "{requestor_email}",
        "Access Granted: {target_system} — {access_level}",
        "Hello {requestor_name},\n\nYour access request has been approved and provisioned.\n\nSystem: {target_system}\nAccess level: {access_level}\nDuration: {access_duration}\nApproved by: {approver_name}\n\nYou should now have access. If you experience any issues, contact the IT helpdesk.\n\nReminder: This access will be automatically revoked on {expiry_date} unless renewed.",
      ),
      slackNode(
        "7",
        "Slack: Confirm access grant in requestor's thread",
        1440,
        "Access Request — Approved\n\nRequestor: {requestor_name}\nSystem: {target_system}\nLevel: {access_level}\nDuration: {access_duration}\nApproval chain: {approval_chain}\n\nAccess has been provisioned via Okta. Auto-revocation scheduled for {expiry_date}.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log to access control audit trail",
        1680,
        "AccessAudit",
        '[["{timestamp}","{requestor_name}","{target_system}","{access_level}","{approval_chain}","{is_privileged}","{access_duration}","{expiry_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes Slack-based access requests by validating against RBAC policies, routing privileged access through manager and security approval, provisioning via Okta, confirming to the requestor via email and Slack, and logging to the access audit trail.",
  },

  // ─── 236. SaaS License Offboarding ────────────────────────────────────

  "SaaS License Offboarding": {
    nodes: [
      trigger(
        "BambooHR webhook: Employee termination processed",
        "webhook_app",
        {
          source: "bamboohr",
          event: "employee.terminated",
          howItStarts:
            "Fires when an employee termination is processed in BambooHR, providing the employee name, email, department, termination date, and manager for SaaS license revocation across all systems.",
          setup:
            "Connect BambooHR, Okta, and Google Workspace admin APIs. Configure the SaaS license inventory spreadsheet.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "runCode",
        "List all active SaaS licenses and integrations for the employee",
        240,
        {
          codeDetails: {
            language: "javascript",
            code: "const employee = inputs.employee_email; const licenses = inputs.active_licenses || ['Okta SSO', 'Google Workspace', 'Slack', 'GitHub', 'Jira', 'Salesforce', 'Zoom', 'Notion']; const has_admin = inputs.admin_accounts || []; const is_admin = has_admin.length > 0; return { license_list: licenses, license_count: licenses.length, has_admin_accounts: is_admin, admin_apps: has_admin.join(', '), employee_name: inputs.employee_name, employee_email: employee, termination_date: inputs.termination_date };",
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "Okta: Deactivate user and revoke all SSO sessions",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{okta_domain}/api/v1/users/{user_id}/lifecycle/deactivate",
            headers: [
              { key: "Authorization", value: "SSWS {okta_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "status", customName: "okta_status", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Google Workspace: Suspend account and transfer Drive ownership",
        480,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://admin.googleapis.com/admin/directory/v1/users/{user_email}",
            headers: [
              { key: "Authorization", value: "Bearer {google_admin_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "suspended", value: "true" },
              { key: "suspensionReason", value: "ADMIN" },
            ],
            variables: [
              {
                name: "suspended",
                customName: "google_suspended",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Check: Employee had admin-level accounts?", 720, {
        conditionDetails: {
          conditions: [
            { variable: "{has_admin_accounts}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "6",
        "approvalGate",
        "Approval: IT lead reviews admin account deprovisioning",
        960,
        {
          approvalDetails: {
            approvers: ["it_lead"],
            instructions:
              "Employee {employee_name} had admin-level access to: {admin_apps}\n\nTermination date: {termination_date}\n\nPlease verify:\n1. Admin credentials have been rotated\n2. Shared service accounts have been updated\n3. No critical automations depend on this user\n\nApprove to complete offboarding.",
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Confirm all licenses deprovisioned",
        1200,
        "SaaS License Offboarding Complete\n\nEmployee: {employee_name} ({employee_email})\nTermination date: {termination_date}\n\nDeprovisioned ({license_count} services):\n{license_list_formatted}\n\nOkta: {okta_status}\nGoogle: {google_suspended}\nAdmin accounts reviewed: {has_admin_accounts}\n\nAll sessions terminated. Data transfer to manager initiated.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log offboarding to license inventory",
        1680,
        "LicenseOffboarding",
        '[["{timestamp}","{employee_name}","{employee_email}","{license_count}","{admin_apps}","{okta_status}","{google_suspended}","{termination_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers on BambooHR termination events, lists all active SaaS licenses, deactivates Okta SSO and suspends Google Workspace, routes admin account deprovisioning through IT lead approval, confirms completion in Slack, and logs to the license inventory.",
  },
};
